/*
  NodeForm.jsx
*/

// Npm.
import React, { useState } from 'react';

// Lib.
import cleanWhiteSpace from '../../../../utils/cleanWhiteSpace';
import getUniqueNodeId from '../../../../utils/getUniqueNodeId';
import getUniqueLinkId from '../../../../utils/getUniqueLinkId';
import deleteNodeFromGraph from '../../../../utils/deleteNodeFromGraph';

// Components.
import TextField from '../../../TextField';
import TextFieldOnBlur from '../../../TextFieldOnBlur';
import ColorPicker from '../../../ColorPicker';
import Selector from '../../../Selector';
import Button from '../../../Button';
import CheckboxWithLabel from '../../../CheckboxWithLabel';
import NodeSelector from '../../../NodeSelector';
import TagsField from './TagsField';
//import PeopleGenerationField from '../PeopleGenerationField';
//import ResourceRequirementsField from './ResourceRequirementsField';
import EditJSONButton from '../EditJSONButton';
import PostProcessingForm from '../PostProcessingForm';
import PeopleGenerationField from '../PeopleGenerationField/PeopleGenerationField';

// Hooks.
import useReadOnly from '../../../../hooks/useReadOnly';

// Makes.
import { makeStyles } from '../../../../makes/makeStyles';

// Constants.
import { DEFAULT_NODE_COLOR } from '../../../../utils/constants';

// Local constants.
const NOOP = () => {};

// Helper to get an option's style for the node selectors.
function getOptionStyle(node) {
  return node ? { color: node._botech?.color || DEFAULT_NODE_COLOR } : undefined;
}

// Helper to get a node's label.
function getNodeLabel(node) {
  return node._botech?.label || 'untitled';
}

// Styles.
const useStyles = makeStyles(theme => ({
  root: {
    '& > .section': {
      marginBottom: theme.space(4),
      '& > .title': {
        color: theme.color_primary_main,
        marginBottom: theme.space(2),
      }
    }
  },
  fieldContainer: {
    marginTop: theme.space(2),
  },
  smallFieldContainer: {
    marginTop: theme.space(1),
  },
  actionsContainer: {
    '& > div': {
      marginTop: theme.space(1),
      '&:first-of-type': {
        marginTop: 0,
      }
    }
  },
}));

/**
 * Node editing form.
 */
export default function NodeForm({
  className,
  node,
  graphData,
  onChangeGraphData,
  onChangeSelectedNode,
  disabled,
}) {

  const readOnly = useReadOnly();
  const classes = useStyles();

  // All fields unrelated to ForceGraph (label, x, y) are kept in "_botech" and are
  // edited independently.

  const botech = node._botech || {};

  // Creating link form and handlers.

  const handleChangeTarget = readOnly ? NOOP : (targetNodeId) => {

    // Make sure the target node exists.
    const targetNode = graphData.nodes.find(nd => nd.id === targetNodeId);
    if (!targetNode) {
      return;
    }

    // Create a link to the target node.
    if (onChangeGraphData) {
      onChangeGraphData(data => ({
        ...data,
        links: data.links.concat({
          id: getUniqueLinkId(data),
          source: node,
          target: targetNode
        })
      }));
    }
  }

  // Helper to update the node with given properties.

  function updateNode(nextNode) {
    if (nextNode !== node) {

      if (onChangeGraphData) {
        onChangeGraphData(data => ({
          ...data,
          nodes: data.nodes.map(obj => obj === node ? nextNode : obj),
          links: data.links.map(link =>
            link.source !== node && link.target !== node ? link : {
              ...link,
              source: link.source === node ? nextNode : link.source,
              target: link.target === node ? nextNode : link.target,
            }
          )
        }));
      }
      
      if (onChangeSelectedNode) {
        onChangeSelectedNode(nextNode);
      }

    }
  }

  function handleChangeBotech(value) {
    updateNode({ ...node, _botech: value || {} });
  }

  const handleClickDelete = readOnly ? NOOP : () => {

    const next = deleteNodeFromGraph(graphData, node);

    if (onChangeGraphData) {
      onChangeGraphData(next);
    }

    // When the user deletes the node, just follow the arrows and select
    // one of the node's targets, or vice versa. Give preference to parents as these are more likely to
    // exist after deletion.
    // The node COULD be deleted, so check if it still exists after we find it.

    const selectNode = graphData.links.find(obj => obj.target === node)?.source
      || graphData.links.find(obj => obj.source === node)?.target
      || next.nodes[0]
      || null;

    if (onChangeSelectedNode && next.nodes.includes(selectNode)) {
      onChangeSelectedNode(selectNode !== node ? selectNode : null);
    }
  };

  const handleClickDuplicate = readOnly ? NOOP : () => {

    // Copy node. TODO: Do a deep copy?

    const clonedNode = { ...node, id: getUniqueNodeId(graphData) };
    const next = { ...graphData };
    next.nodes = next.nodes.concat(clonedNode);

    // Set the node's X and Y position to be slightly different.

    if (clonedNode.x != null && clonedNode.y != null) {
      clonedNode.x += 4;
      clonedNode.y += 4;
    }

    // Copy links that connect to the node and point them to the cloned nodes.
    // Copy to the depth where properties are modified.

    const clonedLinks = next.links
      .filter(link => link.source === node || link.target === node)
      .map(link => ({
        ...link,
        id: getUniqueLinkId(next),
        source: link.source === node ? clonedNode : link.source,
        target: link.target === node ? clonedNode : link.target,
      }));

    if (clonedLinks.length > 0) {
      next.links = next.links.concat(clonedLinks);
    }

    // Call callbacks.

    if (onChangeGraphData) {
      onChangeGraphData(next);
    }

    if (onChangeSelectedNode) {
      onChangeSelectedNode(clonedNode);
    }
  };

  // Update botech metadata handlers.

  function updateBotech(fields) {
    if (fields) {
      updateNode({ ...node, _botech: { ...botech, ...fields } });
    }
  }

  function handleFocusId(event) {
    event.target.select();
  }

  function handleChangeLabel(value) {
    updateBotech({ label: cleanWhiteSpace(value) });
  }

  function handleChangeColor(event) {
    updateBotech({ color: event.target.value });
  }

  function handleChangeDescription(value) {
    updateBotech({ description: cleanWhiteSpace(value) });
  }

  function handleChangeNodeType(event) {
    updateBotech({ node_type: event.target.value });
  }

  function handleChangeArrayType(event) {
    updateBotech({ array_type: event.target.value });
  }

  function handleClickExpand() {
    const next = { ...botech };
    if (next.collapsed) {
      delete next.collapsed;
    } else {
      next.collapsed = true;
    }
    updateNode({ ...node, _botech: next });
  }

  // function handleChangeEntity(value) {
  //   updateBotech({ entity: cleanWhiteSpace(value) });
  // }

  function handleChangeOrder(value) {
    const n = value ? Number(value) : null;
    updateBotech({ order: typeof n === 'number' && !Number.isNaN(n) ? n : null });
  }

  // function handleChangePeopleGeneration(value) {
  //   updateBotech({ people_generation: value });
  // }

  // function handleChangeResourceRequirements(value) {
  //   updateBotech({ resource_requirements: value });
  // }

  // function handleChangeResourceConstraint(value) {
  //   updateBotech({ resource_constraint: value });
  // }

  // function handleChangeResourceGeneration(value) {
  //   updateBotech({ resource_generation: value });
  // }

  function handeChangeAgeing(event) {
    updateBotech({ ageing: event.target.checked });
  }

  function handeChangeFlush(event) {
    updateBotech({ flush: event.target.checked });
  }

  function handleChangeTags(tags) {
    updateBotech({ tags });
  }

  function handleChangeGenerateArray(value) {
    updateBotech({ generate_array: value });
  }

  return (
    <div className={classes.root}>

      <div className="section">
        <p className="title overline">
          Node Properties
        </p>
        <div className={classes.fieldContainer}>
          <TextField
            label="Node ID"
            placeholder="None"
            value={node.id || ''}
            onFocus={handleFocusId}
            readOnly
            fullWidth
          />
        </div>
        <div className={classes.fieldContainer}>
          <TextFieldOnBlur
            label="Label"
            placeholder="None"
            value={botech.label || ''}
            onChange={handleChangeLabel}
            fullWidth
            maxLength={128}
            disabled={disabled}
            readOnly={readOnly}
          />
        </div>
        <div className={classes.fieldContainer}>
          <TextFieldOnBlur
            label="Description"
            placeholder="None"
            value={botech.description || ''}
            onChange={handleChangeDescription}
            fullWidth
            maxLength={512}
            disabled={disabled}
            readOnly={readOnly}
          />
        </div>
        {/*<div className={classes.fieldContainer}>
          <TextFieldOnBlur
            label="Entity"
            value={botech.entity || ''}
            onChange={handleChangeEntity}
            fullWidth
            maxLength={64}
            disabled={disabled}
          />
        </div>*/}
        <div className={classes.fieldContainer}>
          <TextFieldOnBlur
            label="Order"
            type="number"
            placeholder="None"
            value={typeof botech.order === 'number' ? String(botech.order) : ''}
            onChange={handleChangeOrder}
            fullWidth
            disabled={disabled}
            readOnly={readOnly}
          />
        </div>
        <div className={classes.fieldContainer}>
          <Selector
            label="Node Type"
            value={botech.node_type || 'STATE'}
            onChange={handleChangeNodeType}
            fullWidth
            disabled={disabled}
            readOnly={readOnly}
          >
            <option value="STATE">STATE</option>
            <option value="SURROGATE">SURROGATE</option>
            <option value="CONSTANT">CONSTANT</option>
            <option value="FUNCTION">FUNCTION</option>
            <option value="RESOURCE">RESOURCE</option>
            <option value="COST">COST</option>
          </Selector>
        </div>
        <div className={classes.fieldContainer}>
          <Selector
            label="Array Type"
            value={botech.array_type || ''}
            onChange={handleChangeArrayType}
            fullWidth
            disabled={disabled}
            readOnly={readOnly}
          >
            <option value="" disabled>-- select --</option>
            <option value="ARRAY">ARRAY</option>
            <option value="SCALAR">SCALAR</option>
          </Selector>
        </div>
        <div className={classes.fieldContainer}>
          <TagsField
            nodes={graphData.nodes}
            value={botech.tags}
            onChange={handleChangeTags}
            disabled={disabled}
            readOnly={readOnly}
          />
        </div>
        {
          !readOnly &&
          <div className={classes.fieldContainer}>
            <ColorPicker
              label="Colour"
              value={botech.color || null}
              onChange={handleChangeColor}
              disabled={disabled}
              readOnly={readOnly}
            />
          </div>
        }
      </div>

      {/*<div className="section">
        <p className="title overline">
          People Generation
        </p>
        <PeopleGenerationField
          label="People Generation"
          value={botech.people_generation || null}
          onChange={handleChangePeopleGeneration}
          disabled={disabled}
        />
      </div>*/}

      {/*<div className="section">
        <p className="title overline">
          Resource Generation
        </p>
        <ResourceRequirementsField
          label="Resource Generation"
          value={botech.resource_generation || null}
          resources={graphData._botech?.resources || null}
          onChange={handleChangeResourceGeneration}
          disabled={disabled}
        />
      </div>*/}

      {/*<div className="section">
        <p className="title overline">
          Resource Requirements
        </p>
        <ResourceRequirementsField
          label="Resource Requirements"
          value={botech.resource_requirements || null}
          constraint={botech.resource_constraint || null}
          resources={graphData._botech?.resources || null}
          onChange={handleChangeResourceRequirements}
          onChangeConstraint={handleChangeResourceConstraint}
          disabled={disabled}
        />
      </div>*/}

      <div className="section">
        <p className="title overline">
          Behaviours
        </p>
        <div className={classes.smallFieldContainer}>
          <CheckboxWithLabel
            label="Ageing"
            fullWidth
            checked={botech.ageing === true}
            onChange={handeChangeAgeing}
            disabled={disabled}
            readOnly={readOnly}
          />
        </div>
        <div className={classes.smallFieldContainer}>
          <CheckboxWithLabel
            label="Flush"
            fullWidth
            checked={botech.flush === true}
            onChange={handeChangeFlush}
            disabled={disabled}
            readOnly={readOnly}
          />
        </div>
      </div>

      <div className="section">
        <p className="title overline">
          Postprocessing
        </p>
        <PostProcessingForm
          botech={botech}
          onChangeBotech={handleChangeBotech}
          disabled={disabled}
          readOnly={readOnly}
        />
      </div>

      <div className="section">
        <p className="title overline">
          Generate Array
        </p>
        <PeopleGenerationField
          label="Generate Array"
          value={botech.generate_array}
          onChange={handleChangeGenerateArray}
          nodes={graphData.nodes}
          disabled={disabled}
          readOnly={readOnly}
        />
      </div>

      {
        !readOnly &&
        <div className="section">
          <p className="title overline">
            Create a Link
          </p>
          <div className={classes.fieldContainer}>
            <NodeSelector
              nodes={graphData.nodes}
              fullWidth
              placeholder="Select a Target Node"
              disabled={disabled}
              value={null}
              onChange={handleChangeTarget}
            />
          </div>
        </div>
      }

      <div className="section">
        <p className="title overline">
          Node Actions
        </p>
        <div className={classes.actionsContainer}>
          <div>
            <Button onClick={handleClickExpand} disabled={disabled} fullWidth>
              { `${botech.collapsed ? 'Expand' : 'Collapse'} Node` }
            </Button>
          </div>
          {
            !readOnly &&
            <>
              <div>
                <EditJSONButton
                  value={botech}
                  onChange={handleChangeBotech}
                  disabled={disabled}
                  fullWidth
                >
                  Edit Raw Node
                </EditJSONButton>
              </div>
              <div>
                <Button onClick={handleClickDuplicate} disabled={disabled} fullWidth>
                  Duplicate Node
                </Button>
              </div>
              <div>
                <Button onClick={handleClickDelete} disabled={disabled} fullWidth>
                  Delete Node
                </Button>
              </div>
            </>
          }
        </div>
      </div>

    </div>
  );
}
